/**
 * Catalogue des variables d'e-mail, rangées par famille — une seule description pour tous les écrans.
 *
 * Chaque écran d'e-mail passe toujours sa propre liste de variables (celles que son envoi sait remplir,
 * `{ token: '{{date}}', label: '…' }`). Ce catalogue ne remplace pas cette liste : il lui donne un
 * rangement (famille), un nom court et un exemple. Une variable du catalogue absente de la liste de
 * l'écran est montrée grisée (elle n'existe pas pour cet e-mail) ; une variable de la liste absente du
 * catalogue reste utilisable, rangée dans « Autres informations » sous le libellé que l'écran lui donne.
 */

export type FamilleVariable = 'client' | 'rdv' | 'equipe' | 'paiement' | 'etab' | 'lien' | 'autres';

export interface InfoFamille {
    nom: string;
    aide: string;
    /** Couleur de la pastille — light-dark() suit le thème clair/sombre posé par l'application. */
    couleur: string;
}

export const FAMILLES: Record<FamilleVariable, InfoFamille> = {
    client: { nom: 'Le client', aide: "La personne qui reçoit l'e-mail.", couleur: 'light-dark(#2159c7, #6fa3c9)' },
    rdv: { nom: 'Le rendez-vous', aide: 'Quand, où et quoi.', couleur: 'light-dark(#1f6b45, #5fc794)' },
    equipe: { nom: "L'intervenant", aide: "Le salarié qui s'occupe du client.", couleur: 'light-dark(#7b3fb0, #b98be6)' },
    paiement: { nom: 'Le paiement', aide: 'Montants et échéances.', couleur: 'light-dark(#a2670a, #e0b85c)' },
    etab: { nom: 'Mon établissement', aide: 'Vos coordonnées, toujours les mêmes.', couleur: 'light-dark(#6b5a45, #c2ab8b)' },
    lien: { nom: 'Boutons et liens', aide: "Un élément cliquable : à afficher en bouton ou en lien dans le texte.", couleur: 'light-dark(#c0392b, #f08c82)' },
    autres: { nom: 'Autres informations', aide: 'Propres à cet e-mail.', couleur: 'light-dark(#5b6672, #8a94a1)' },
};

export const ORDRE_FAMILLES: FamilleVariable[] = ['client', 'rdv', 'equipe', 'paiement', 'etab', 'lien', 'autres'];

export interface InfoVariable {
    famille: FamilleVariable;
    nom: string;
    exemple: string;
    /** Cliquable : le choix « bouton » ou « lien » se fait à l'insertion. */
    cliquable?: boolean;
    image?: boolean;
    /** Pourquoi la variable peut ne rien afficher — montré en étiquette « peut être vide ». */
    videSi?: string;
}

/** Codes dont le sens change d'un e-mail à l'autre : le libellé donné par l'écran prévaut sur celui-ci. */
const SENS_PROPRE_A_L_ECRAN = new Set(['prenom', 'nom', 'montant', 'motif', 'date', 'document', 'articles', 'bouton']);

export const CATALOGUE_VARIABLES: Record<string, InfoVariable> = {
    client_nom: { famille: 'client', nom: 'Nom complet', exemple: 'Camille Martin' },
    prenom: { famille: 'client', nom: 'Prénom', exemple: 'Camille' },
    nom: { famille: 'client', nom: 'Nom', exemple: 'Martin' },
    client_telephone: { famille: 'client', nom: 'Téléphone', exemple: '06 12 34 56 78' },
    client_email: { famille: 'client', nom: 'E-mail', exemple: 'camille.martin@mail.fr' },
    adresse: { famille: 'client', nom: 'Adresse', exemple: '12 rue des Lilas, 69003 Lyon' },
    fidelite: { famille: 'client', nom: 'Carte de fidélité', exemple: '8 passages sur 10', videSi: 'la carte de fidélité est éteinte' },

    date: { famille: 'rdv', nom: 'Date', exemple: 'jeudi 14 novembre' },
    heure: { famille: 'rdv', nom: 'Heure de début', exemple: '10 h 30' },
    horaire: { famille: 'rdv', nom: 'Plage horaire', exemple: '10 h 30 – 12 h 00' },
    lieu_rdv: { famille: 'rdv', nom: 'Lieu', exemple: 'Chez le client' },
    prestation: { famille: 'rdv', nom: 'Prestation', exemple: 'Nettoyage canapé 3 places' },
    prestation_prix: { famille: 'rdv', nom: 'Prix de la prestation', exemple: '89,00 €' },
    prestation_duree: { famille: 'rdv', nom: 'Durée', exemple: '1 h 30' },
    annulation: { famille: 'rdv', nom: "Règle d'annulation", exemple: "Annulation gratuite jusqu'à 24 h avant", videSi: "aucune règle n'est activée dans Horaires" },
    motif: { famille: 'rdv', nom: 'Motif', exemple: 'Imprévu de dernière minute' },

    salarie_prenom: { famille: 'equipe', nom: 'Prénom', exemple: 'Léa' },
    salarie_nom: { famille: 'equipe', nom: 'Nom', exemple: 'Bernard' },
    salarie_nom_complet: { famille: 'equipe', nom: 'Nom complet', exemple: 'Léa Bernard' },
    salarie_telephone: { famille: 'equipe', nom: 'Téléphone', exemple: '06 98 76 54 32' },
    salarie_email: { famille: 'equipe', nom: 'E-mail', exemple: 'lea.bernard@exemple.fr' },
    salaries: { famille: 'equipe', nom: "Toute l'équipe prévue", exemple: 'Léa Bernard, Hugo Petit' },

    total: { famille: 'paiement', nom: 'Total', exemple: '89,00 €' },
    montant: { famille: 'paiement', nom: 'Montant', exemple: '89,00 €' },
    deja_regle: { famille: 'paiement', nom: 'Déjà réglé', exemple: '30,00 €' },
    montant_regle: { famille: 'paiement', nom: 'Montant réglé', exemple: '30,00 €' },
    reste_a_payer: { famille: 'paiement', nom: 'Reste à payer', exemple: '59,00 €' },
    moyen_paiement: { famille: 'paiement', nom: 'Moyen de paiement', exemple: 'Carte bancaire' },
    date_echeance: { famille: 'paiement', nom: 'Date limite de paiement', exemple: '28 novembre' },
    statut_paiement: { famille: 'paiement', nom: 'Statut du paiement', exemple: 'Acompte versé' },

    entreprise: { famille: 'etab', nom: 'Nom', exemple: 'Shampouine' },
    entreprise_telephone: { famille: 'etab', nom: 'Téléphone', exemple: '04 72 00 00 00' },
    entreprise_email: { famille: 'etab', nom: 'E-mail', exemple: 'contact@shampouine.fr' },
    entreprise_adresse: { famille: 'etab', nom: 'Adresse', exemple: '8 quai Saint-Antoine, 69002 Lyon' },
    entreprise_tva: { famille: 'etab', nom: 'N° de TVA', exemple: 'FR12 123456789' },
    entreprise_siret: { famille: 'etab', nom: 'SIRET', exemple: '123 456 789 00012' },
    logo: { famille: 'etab', nom: 'Logo', exemple: 'Votre logo', image: true },

    lien_rdv: { famille: 'lien', nom: 'Prendre rendez-vous', exemple: 'Prendre rendez-vous', cliquable: true },
    lien_annulation: { famille: 'lien', nom: 'Annuler le rendez-vous', exemple: 'Annuler le rendez-vous', cliquable: true, videSi: "l'annulation n'est pas autorisée" },
    lien_paiement: { famille: 'lien', nom: 'Payer en ligne', exemple: 'Payer en ligne', cliquable: true, videSi: "le paiement en ligne n'est pas configuré ou tout est réglé" },
    lien_avis: { famille: 'lien', nom: 'Donner mon avis', exemple: 'Donner mon avis', cliquable: true, videSi: "le module Avis n'est pas actif" },
    bouton: { famille: 'lien', nom: "Bouton d'action", exemple: 'Voir en ligne', cliquable: true },
};

/** Variables dont le serveur sait faire un simple lien (`{{lien_paiement:lien}}`) — voir normaliserModesLien. */
export const JETONS_BOUTON_OU_LIEN = new Set(['lien_annulation', 'lien_paiement', 'lien_avis', 'lien_rdv', 'bouton']);

export interface VariableAffichee {
    code: string;
    famille: FamilleVariable;
    nom: string;
    exemple: string;
    cliquable: boolean;
    image: boolean;
    videSi?: string;
    /** Existe dans l'e-mail ouvert. Sinon : montrée grisée, non insérable. */
    disponible: boolean;
}

const codeDe = (token: string) => token.replace(/^\{\{|\}\}$/g, '');

/** Famille d'une variable inconnue du catalogue ou ambiguë, d'après le libellé donné par l'écran. */
const familleSelonLibelle = (code: string, libelle: string): FamilleVariable => {
    if (code === 'prenom' || code === 'nom') return /salari|intervenant|employ/i.test(libelle) ? 'equipe' : 'client';
    if (/^lien_|^bouton/.test(code)) return 'lien';
    return 'autres';
};

/** Fusionne la liste de l'écran et le catalogue : tout le catalogue + les variables propres à l'écran. */
export interface OptionsVariables {
    /** Écrans hors rendez-vous (alerte de service, devis, événement…) : ne montrer que leurs variables,
     *  sous leur propre libellé, au lieu de tout le catalogue avec des grisés hors sujet. */
    seulementEcran?: boolean;
}

export function construireVariables(tokensEcran: { token: string; label: string }[], options: OptionsVariables = {}): VariableAffichee[] {
    const dispo = new Map(tokensEcran.map((t) => [codeDe(t.token), t.label]));
    const out: VariableAffichee[] = [];
    const vus = new Set<string>();

    Object.entries(CATALOGUE_VARIABLES).forEach(([code, info]) => {
        vus.add(code);
        const libelleEcran = dispo.get(code);
        if (options.seulementEcran && !libelleEcran) return;
        const propre = (SENS_PROPRE_A_L_ECRAN.has(code) || options.seulementEcran) && libelleEcran;
        out.push({
            code,
            // Seuls prénom et nom changent de famille selon l'écran (client ou salarié) ; les autres gardent la leur.
            famille: propre && (code === 'prenom' || code === 'nom') ? familleSelonLibelle(code, libelleEcran) : info.famille,
            nom: propre ? libelleEcran : info.nom,
            exemple: info.exemple,
            cliquable: !!info.cliquable,
            image: !!info.image,
            videSi: info.videSi,
            disponible: dispo.has(code),
        });
    });
    tokensEcran.forEach((t) => {
        const code = codeDe(t.token);
        if (vus.has(code)) return;
        out.push({
            code, famille: familleSelonLibelle(code, t.label), nom: t.label, exemple: '', cliquable: /^lien_|^bouton/.test(code),
            image: false, disponible: true,
        });
    });
    return out;
}
